import React, {
  FC,
  forwardRef,
  useRef,
  useEffect,
  KeyboardEvent,
  useState,
  useMemo,
  useCallback,
  FocusEventHandler,
  ChangeEvent,
  RefAttributes
} from 'react';
import { time } from 'uniqid';
import { getSelectedIndex, menuItems, buildHandler, setDefaultAutoSuggest } from './dropdown.utils';
import { defaultTo, findIndex, get, isEmpty } from 'lodash';
import { DropdownListProps, DropdownList, DropdownItemProps } from './DropdownList';
import { Entry } from '../Entry';
import { Error } from '../Error';
import { KEY_CHARS, SELECT_TRANSLATION, SUPPORTED_LOCALES } from '@trip-ux/constants';
import { Tooltip } from '../Tooltip';
import { showComponent } from '@trip-ux/common';
import { DropdownSelect } from './DropdownSelect';

export enum DropdownType {
  AUTO_SUGGEST = 'AUTO_SUGGEST',
  DROP_DOWN = 'DROP_DOWN'
}

export interface DropdownProps extends DropdownListProps {
  children?: React.ReactNode;
  name: string;
  id?: string;
  className?: string;
  isDisabled?: boolean;
  error?: string;
  value: string | null;
  onChange: (value: string, idx?: number) => void;
  onBlur?: (e: string | React.FocusEvent) => void;
  onInputBlur?: FocusEventHandler;
  onFocus?: FocusEventHandler;
  showError?: boolean;
  variant?: DropdownType;
  onAutoSuggestChange?: (value: string) => void;
  validateOnActive?: boolean;
  isRequired?: boolean;
  tooltipContent?: string;
  ariaDescribedby?: string;
  ariaLabel?: string;
  language?: SUPPORTED_LOCALES;
  shouldSuggestWithoutTyping?: boolean;
  resetAutoSuggest?: boolean;
}

const ToolTipContent = (tooltipContent, name, id) =>
  !isEmpty(tooltipContent) ? (
    <Tooltip
      id={`${name}-tooltip-${id}`}
      ariaLabel={`${name}-tooltip`}
      bubbleWidth={200}
      title={tooltipContent}
      hover={false}
      hasCloseButton={true}
      closeButtonAria={`close-${name}-tooltip`}
    />
  ) : null;

const isShowDrop = (expanded, options) => expanded && !isEmpty(options);
const isItemClicked = (isAutoSuggest, dropDownFocus) => !isAutoSuggest && dropDownFocus.current.focus();
const hasOnBlurValue = (items, selected) => (items[selected] ? items[selected].value : undefined);
const findIdx = (items: DropdownItemProps[], key) =>
  findIndex(items, item => !item.isDisabled && !isEmpty(item.label.match(new RegExp(`^${key}`, 'i'))));
const hasError = (error, isDisabled) => !isEmpty(error) && !isDisabled;

const getActiveDescendant = (options, id, browsedIndex, error, isDisabled) => {
  let activeDescendant = '';
  if (hasError(error, isDisabled) && browsedIndex <= 0) {
    activeDescendant = `${id}-described`;
  } else {
    const browsedOption = options && options[browsedIndex - 1];
    if (browsedOption && browsedOption.value) {
      activeDescendant = `dropdown-list=${id}=${browsedOption.value}`;
    }
  }
  return activeDescendant;
};

const handleExpandItem = (items, key, setExpanded, setBrowsedIndex) => {
  const idx = findIdx(items, key);
  if (idx !== -1) {
    setBrowsedIndex(idx);
    setExpanded(true);
  }
};

const getAriaDescribedValue = (ariaDescribedby, id) => (ariaDescribedby ? ariaDescribedby : `${id}-described`);
const handleCloseDropdown = (e, setExpanded, id) => {
  if (get(e.relatedTarget, 'id') !== `dropdown-list-${id}`) {
    setExpanded(false);
  }
};

const regex = /^[\p{L}\p{N}.-]*$/u;

export const Dropdown: FC<DropdownProps & RefAttributes<HTMLInputElement>> = forwardRef<
  HTMLInputElement,
  DropdownProps
>(
  (
    {
      name,
      id = time('dropdown-'),
      value = '',
      className = '',
      options,
      isDisabled = false,
      onChange,
      placeholder,
      isRequired,
      error,
      selectedIndex,
      onBlur = () => true,
      onFocus = () => true,
      onInputBlur = () => true,
      children,
      showError = true,
      variant = DropdownType.DROP_DOWN,
      onAutoSuggestChange,
      validateOnActive = true,
      tooltipContent,
      ariaDescribedby,
      ariaLabel,
      language,
      shouldSuggestWithoutTyping,
      resetAutoSuggest = false
    },
    ref
  ) => {
    const defaultPlaceholder = defaultTo(
      placeholder,
      language === SUPPORTED_LOCALES.ENGLISH ? SELECT_TRANSLATION.ENGLISH : SELECT_TRANSLATION.FRENCH
    );

    const items = useMemo(() => menuItems(options, defaultPlaceholder, value), [options, defaultPlaceholder, value]);
    const [expanded, setExpanded] = useState(false);
    const [selected, setSelected] = useState(getSelectedIndex(items, value, selectedIndex));
    const [browsedIndex, setBrowsedIndex] = useState(selected);

    const isAutoSuggest = variant === DropdownType.AUTO_SUGGEST;
    const [autoSuggestValue, setAutoSuggestValue] = useState(setDefaultAutoSuggest(defaultPlaceholder, value));

    const showDrop = isShowDrop(expanded, options);

    useEffect(() => {
      if (resetAutoSuggest) {
        setAutoSuggestValue('');
      }
    }, [resetAutoSuggest]);

    useEffect(() => setSelected(getSelectedIndex(items, value, selectedIndex)), [items, selectedIndex, value]);

    useEffect(() => {
      !value && isAutoSuggest && setAutoSuggestValue('');
    }, [value, isAutoSuggest]);

    useEffect(() => {
      if (isAutoSuggest) {
        const selIdx = items.findIndex(item => item.value === value);
        if (items.length > 0 && selIdx !== -1) {
          setSelected(selIdx);
          setBrowsedIndex(selIdx);
        } else {
          setSelected(-1);
          setBrowsedIndex(-1);
        }
      }
    }, [items, isAutoSuggest, value]);

    const handleChange = useCallback(
      (_, ind: number) => {
        if (items.length > ind && items[ind] && !items[ind].isDisabled) {
          setSelected(ind);
          isAutoSuggest && setAutoSuggestValue(items[ind].label);
        }
        onBlur(hasOnBlurValue(items, selected));
      },
      [items, onBlur, onChange, selected, isAutoSuggest]
    );

    const dropDownFocus = useRef(null);

    const handleItemClick = useCallback(
      (_, ind: number) => {
        handleChange(_, ind);
        onChange(items[ind].value, ind);
        setExpanded(!expanded);
        isItemClicked(isAutoSuggest, dropDownFocus);
        onBlur(items[ind].value);
      },
      [handleChange, expanded, setExpanded, isAutoSuggest]
    );

    const handleEnterOrSpace = result => {
      const indexToSelect = typeof result === 'number' ? result : browsedIndex;
      setSelected(indexToSelect);
      onChange(items[indexToSelect].value, indexToSelect);
      if (isAutoSuggest) {
        setAutoSuggestValue(items[indexToSelect].label);
      }
      onBlur(items[indexToSelect].value);
      isItemClicked(isAutoSuggest, dropDownFocus);
      setExpanded(false);
    };

    const handleArrowKeys = result => {
      const indexToSelect = typeof result === 'number' ? result : browsedIndex;
      setSelected(indexToSelect);
      if (isAutoSuggest && items[indexToSelect]) {
        setAutoSuggestValue(items[indexToSelect].label);
      }
    };

    const handleKeyboardEvent = (e: KeyboardEvent) => {
      const { key } = e;
      const handler = buildHandler(
        e,
        items,
        selected,
        browsedIndex,
        defaultPlaceholder,
        {
          handleChange,
          setExpanded,
          setBrowsedIndex
        },
        { isExpanded: expanded, isAutoSuggest }
      );
      if (handler[key]) {
        e.preventDefault();
        const result = handler[key]();

        if (key === KEY_CHARS.ENTER || key === KEY_CHARS.SPACE) {
          handleEnterOrSpace(result);
        }

        if (key === KEY_CHARS.ARROW_UP || key === KEY_CHARS.ARROW_DOWN) {
          handleArrowKeys(result);
        }
      }
    };

    const handleOnBlur = e => {
      handleInputBlur(e);
      onBlur(e);
    };

    const handleListboxBlur = () =>
      setTimeout(() => {
        setExpanded(false);
        onBlur(hasOnBlurValue(items, selected));
      }, 0);

    const handleInputBlur = (e: React.FocusEvent) => {
      handleCloseDropdown(e, setExpanded, id);
      onInputBlur(e);
    };

    const onChangeAutoSuggest = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        const val = e.target.value;
        setExpanded(true);
        onAutoSuggestChange(val);
        setAutoSuggestValue(val);
      },
      [onAutoSuggestChange]
    );
    return (
      <div className={`${className} dropdown`}>
        {showComponent(!isAutoSuggest, <input name={name} id={`dropdown-input-${id}`} type="hidden" value={value} />)}
        <div className="d-flex flex-row flex-wrap">
          <label htmlFor={id} id={`dropdown-label-${id}`} className="mr-1">
            {children}
          </label>
          {ToolTipContent(tooltipContent, name, id)}
        </div>
        <div className="dropdown-wrapper">
          {showComponent(
            !isAutoSuggest,
            <DropdownSelect
              id={id}
              ariaLabelledby={`dropdown-label-${id}`}
              ariaDescribedby={getAriaDescribedValue(ariaDescribedby, id)}
              options={items}
              error={error}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              isRequired={isRequired}
              selectedIndex={selected}
              isDisabled={isDisabled}
              value={value}
              aria-haspopup="listbox"
              aria-expanded={!!expanded}
            />
          )}
          {showComponent(
            isAutoSuggest,
            <Entry
              type="text"
              className={`drop-input ${hasError(error, isDisabled) ? 'error' : ''}`}
              role="combobox"
              aria-controls={`dropdown-list-${id}`}
              onChange={e => {
                setBrowsedIndex(0);
                onChangeAutoSuggest(e);
                !validateOnActive && onChange(e.target.value);
              }}
              placeholder={defaultPlaceholder}
              value={autoSuggestValue}
              onFocus={e => {
                shouldSuggestWithoutTyping && setExpanded(true);
                onFocus(e);
              }}
              onBlur={handleOnBlur}
              onKeyDown={handleKeyboardEvent}
              autoComplete="on"
              ref={ref}
              name={name}
              id={id}
              data-testid={id}
              disabled={isDisabled}
              aria-invalid={hasError(error, isDisabled)}
              aria-required={isRequired}
              aria-describedby={`${id}-described`}
              aria-label={ariaLabel}
              aria-haspopup="listbox"
              aria-expanded={!!expanded}
              aria-activedescendant={expanded?getActiveDescendant(options, id, browsedIndex, error, isDisabled):undefined}
              tabIndex={0}
            />
          )}
          {showComponent(
            showDrop,
            <DropdownList
              id={`dropdown-list-${id}`}
              data-testid={`dropdown-list-${id}`}
              options={items}
              selectedIndex={selected}
              ariaLabelledby={`dropdown-label-${id}`}
              onBlur={handleListboxBlur}
              onKeyDown={handleKeyboardEvent}
              onChange={handleItemClick}
              placeholder={defaultPlaceholder}
              isAutoSuggest={isAutoSuggest}
              hasError={hasError(error, isDisabled)}
              required={isRequired}
              browsedIndex={browsedIndex}
              setBrowsedIndex={setBrowsedIndex}
              role="listbox"
            />
          )}
        </div>
        <Error errors={error} callout={false} showError={showError} id={`${id}-described`} />
      </div>
    );
  }
);

Dropdown.displayName = 'Dropdown';
export default Dropdown;
